import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Pencil,
  Trash2,
  ArrowUp,
  ArrowDown,
  ExternalLink,
  Loader2,
  Save,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { COLLECTION_META } from "./collectionSchemas";

// Build a blank form object based on the schema fields.
const blankForm = (fields) => {
  const obj = {};
  fields.forEach((f) => {
    if (f.type === "switch") obj[f.key] = false;
    else if (f.type === "array") obj[f.key] = "";
    else obj[f.key] = "";
  });
  return obj;
};

// Convert backend row -> form values (arrays back to multi-line text)
const rowToForm = (row, fields) => {
  const obj = {};
  fields.forEach((f) => {
    const v = row?.[f.key];
    if (f.type === "switch") obj[f.key] = Boolean(v);
    else if (f.type === "array") obj[f.key] = Array.isArray(v) ? v.join("\n") : v || "";
    else obj[f.key] = v ?? "";
  });
  return obj;
};

// Convert form values -> payload (arrays back to lists, drop empties optionally)
const formToPayload = (form, fields) => {
  const out = {};
  fields.forEach((f) => {
    const v = form[f.key];
    if (f.type === "array") {
      out[f.key] = (v || "")
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean);
    } else if (f.type === "switch") {
      out[f.key] = Boolean(v);
    } else {
      out[f.key] = v ?? "";
    }
  });
  return out;
};

export default function AdminCollection({ slug: propSlug }) {
  const params = useParams();
  const slug = propSlug || params.slug;
  const meta = COLLECTION_META[slug];
  const { apiFetch } = useAuth();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // row being edited, null = create
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fields = useMemo(() => meta?.fields || [], [meta]);

  const load = async () => {
    if (!meta) return;
    setLoading(true);
    try {
      const data = await apiFetch("GET", `/api/admin/collections/${slug}`);
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(`Failed to load ${meta.title}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (!meta) {
    return <div className="text-muted-foreground">Unknown collection: {slug}</div>;
  }

  const openCreate = () => {
    setEditing(null);
    setForm(blankForm(fields));
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm(rowToForm(row, fields));
    setDialogOpen(true);
  };

  const handleField = (key) => (eOrValue) => {
    const v = typeof eOrValue === "object" && eOrValue?.target ? eOrValue.target.value : eOrValue;
    setForm((prev) => ({ ...prev, [key]: v }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    // Required field validation
    for (const f of fields) {
      if (f.required && !String(form[f.key] || "").trim()) {
        toast.error(`${f.label} is required`);
        return;
      }
    }
    setSaving(true);
    const payload = formToPayload(form, fields);
    try {
      if (editing?.id) {
        await apiFetch("PUT", `/api/admin/collections/${slug}/${editing.id}`, payload);
        toast.success("Updated");
      } else {
        await apiFetch("POST", `/api/admin/collections/${slug}`, payload);
        toast.success("Created");
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Save failed";
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await apiFetch("DELETE", `/api/admin/collections/${slug}/${confirmDelete.id}`);
      toast.success("Deleted");
      setConfirmDelete(null);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Delete failed";
      toast.error(detail);
    }
  };

  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= items.length) return;
    const newOrder = [...items];
    const [moved] = newOrder.splice(index, 1);
    newOrder.splice(target, 0, moved);
    setItems(newOrder); // optimistic
    try {
      await apiFetch("PUT", `/api/admin/collections/${slug}/reorder`, {
        ids: newOrder.map((x) => x.id),
      });
    } catch (err) {
      toast.error("Reorder failed");
      await load();
    }
  };

  return (
    <div className="max-w-5xl space-y-6" data-testid={`admin-collection-${slug}`}>
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <span className="mono-label text-primary text-[10px]">Editing</span>
          <h2 className="text-2xl font-semibold mt-1" style={{ fontFamily: "var(--font-display)" }}>
            {meta.title}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">{meta.description}</p>
        </div>
        <Button onClick={openCreate} data-testid={`admin-${slug}-add-button`}>
          <Plus className="w-4 h-4 mr-2" /> Add new
        </Button>
      </header>

      {loading ? (
        <div className="flex items-center justify-center min-h-[30vh]">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : items.length === 0 ? (
        <Card className="p-10 bg-card border-border text-center">
          <p className="text-muted-foreground">No items yet. Click “Add new” to create one.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((row, idx) => (
            <Card
              key={row.id}
              className="p-4 bg-card border-border flex items-center gap-3"
              data-testid={`admin-${slug}-row-${idx}`}
            >
              <div className="flex flex-col gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  disabled={idx === 0}
                  onClick={() => move(idx, -1)}
                  aria-label="Move up"
                  data-testid={`admin-${slug}-move-up-${idx}`}
                >
                  <ArrowUp className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  disabled={idx === items.length - 1}
                  onClick={() => move(idx, 1)}
                  aria-label="Move down"
                  data-testid={`admin-${slug}-move-down-${idx}`}
                >
                  <ArrowDown className="w-4 h-4" />
                </Button>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold truncate">{row[meta.primaryKey] || "(untitled)"}</h3>
                  {row.type && (
                    <Badge variant="secondary" className="text-[10px]">
                      {row.type}
                    </Badge>
                  )}
                  {row.current && (
                    <Badge variant="outline" className="text-[10px] text-primary border-primary/30">
                      Current
                    </Badge>
                  )}
                </div>
                {row.description && (
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{row.description}</p>
                )}
                {row.title && row.company && !row.description && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {row.title}
                    {row.dates ? ` · ${row.dates}` : ""}
                  </p>
                )}
              </div>

              {row.url && (
                <Button
                  asChild
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  data-testid={`admin-${slug}-visit-${idx}`}
                >
                  <a href={row.url} target="_blank" rel="noreferrer" aria-label="Visit">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => openEdit(row)}
                aria-label="Edit"
                data-testid={`admin-${slug}-edit-${idx}`}
              >
                <Pencil className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-rose-400 hover:text-rose-300"
                onClick={() => setConfirmDelete(row)}
                aria-label="Delete"
                data-testid={`admin-${slug}-delete-${idx}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </Card>
          ))}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid={`admin-${slug}-dialog`}>
          <DialogHeader>
            <DialogTitle>
              {editing ? `Edit ${meta.title} item` : `Add new ${meta.title} item`}
            </DialogTitle>
            <DialogDescription>
              {editing ? "Update fields and save." : "Fill in the fields and save to create."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSave} className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            {fields.map((f) => (
              <div key={f.key} className="space-y-2">
                <Label htmlFor={`field-${f.key}`}>
                  {f.label}
                  {f.required && <span className="text-primary ml-1">*</span>}
                </Label>

                {f.type === "text" && (
                  <Input
                    id={`field-${f.key}`}
                    value={form[f.key] || ""}
                    onChange={handleField(f.key)}
                    placeholder={f.placeholder}
                    data-testid={`admin-${slug}-field-${f.key}`}
                  />
                )}
                {f.type === "textarea" && (
                  <Textarea
                    id={`field-${f.key}`}
                    rows={3}
                    value={form[f.key] || ""}
                    onChange={handleField(f.key)}
                    placeholder={f.placeholder}
                    data-testid={`admin-${slug}-field-${f.key}`}
                  />
                )}
                {f.type === "array" && (
                  <Textarea
                    id={`field-${f.key}`}
                    rows={4}
                    value={form[f.key] || ""}
                    onChange={handleField(f.key)}
                    placeholder={"One item per line"}
                    data-testid={`admin-${slug}-field-${f.key}`}
                  />
                )}
                {f.type === "switch" && (
                  <div className="flex items-center gap-3">
                    <Switch
                      id={`field-${f.key}`}
                      checked={Boolean(form[f.key])}
                      onCheckedChange={(v) => setForm((p) => ({ ...p, [f.key]: v }))}
                      data-testid={`admin-${slug}-field-${f.key}`}
                    />
                    <span className="text-sm text-muted-foreground">
                      {form[f.key] ? "Yes" : "No"}
                    </span>
                  </div>
                )}
                {f.type === "select" && (
                  <Select
                    value={form[f.key] || ""}
                    onValueChange={(v) => setForm((p) => ({ ...p, [f.key]: v }))}
                  >
                    <SelectTrigger data-testid={`admin-${slug}-field-${f.key}`}>
                      <SelectValue placeholder="Choose…" />
                    </SelectTrigger>
                    <SelectContent>
                      {(f.options || []).map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            ))}

            <DialogFooter className="pt-2">
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid={`admin-${slug}-save-button`}>
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" /> {editing ? "Update" : "Create"}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={Boolean(confirmDelete)} onOpenChange={(open) => !open && setConfirmDelete(null)}>
        <AlertDialogContent data-testid={`admin-${slug}-delete-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this item?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove <strong>{confirmDelete?.[meta.primaryKey]}</strong>. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-rose-500 hover:bg-rose-600 text-white"
              data-testid={`admin-${slug}-confirm-delete`}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
